import { ArrowLeft, Braces, MessageSquareText } from "lucide-react";
import * as React from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Page } from "@/components/layout/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch, Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import {
  LANGUAGE_NAME, MAX_NAME_CHARS, MERGE_FIELDS, asSent, channelOf, fillText, liveUses, optOutNote, sampleValues,
  slugFrom, smsParts, templatePayload, templateProblem, type MergeField, type TemplateLanguage, type TextTemplate,
} from "@/lib/text-templates";
import {
  useCreateTextTemplate, useSaveTextTemplate, useTextTemplate, useTextTemplateActive,
} from "@/lib/text-templates-api";
import { cn, formatDateTime } from "@/lib/utils";
import { useWorkflowCatalog } from "@/lib/workflows-api";

/**
 * One text template (ACL-385, ADR-111 in platform), new or kept. The English text is needed, the
 * Spanish one is optional, and a lead's language picks which they get. A lead's details go in as
 * fields from the buttons over each text; the preview fills them with a sample lead and shows the
 * text as it leaves, with the opt-out line under a first text, its length and its SMS parts. The
 * preview shows the text as a number's first or a later one; that is not kept with the template, as
 * it is the number's (ACL-589).
 *
 * Save sends the `updated_at` it read, so a save over somebody else's newer one is a 409 the page
 * explains. The slug is made from the name when the template is made and fixed after: a workflow
 * names the template by it. A template a published workflow sends cannot be turned off.
 */
export function TextTemplatePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const tenantId = params.get("tenant") ? Number(params.get("tenant")) : undefined;
  const isNew = slug === undefined || slug === "new";
  const { data, isPending, error, refetch } = useTextTemplate(isNew ? undefined : slug, tenantId);

  if (!isNew && isPending) {
    return (
      <Page>
        <div className="grid place-items-center py-24">
          <Spinner />
        </div>
      </Page>
    );
  }
  if (!isNew && error && !data) {
    return (
      <Page>
        {error instanceof ApiError && error.status === 404 ? (
          <EmptyState
            title="No such template"
            description="It may belong to another organisation."
            action={
              <Button variant="outline" asChild>
                <Link to="/text-templates">All text templates</Link>
              </Button>
            }
          />
        ) : (
          <ErrorState
            error={error}
            title="Could not load the template"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        )}
      </Page>
    );
  }
  // An email template has its own page, with its subjects (ACL-378).
  if (!isNew && data && channelOf(data) === "email") {
    return <Navigate to={`/email-templates/${encodeURIComponent(data.slug)}${tenantId ? `?tenant=${tenantId}` : ""}`} replace />;
  }
  return <Editor key={data?.slug ?? "new"} template={isNew ? null : (data ?? null)} tenantId={tenantId} />;
}

interface Values {
  name: string;
  en: string;
  es: string;
}

const valuesOf = (t: TextTemplate | null): Values => ({ name: t?.name ?? "", en: t?.texts.en ?? "", es: t?.texts.es ?? "" });
const same = (a: Values, b: Values) => a.name === b.name && a.en === b.en && a.es === b.es;
const STALE = "Somebody else saved this template";

function Editor({ template, tenantId }: { template: TextTemplate | null; tenantId?: number }) {
  const navigate = useNavigate();
  const toast = useToast();
  const catalog = useWorkflowCatalog(tenantId);
  const fields: readonly MergeField[] = catalog.data?.merge_fields.length ? catalog.data.merge_fields : MERGE_FIELDS;
  const create = useCreateTextTemplate(tenantId);
  const save = useSaveTextTemplate(tenantId);
  const active = useTextTemplateActive(tenantId);
  const reload = useTextTemplate(template?.slug, tenantId);
  const [baseline, setBaseline] = React.useState(() => valuesOf(template));
  const [values, setValues] = React.useState(baseline);
  const [readAt, setReadAt] = React.useState(template?.updated_at);
  const [tried, setTried] = React.useState(false);
  const [serverProblem, setServerProblem] = React.useState<string | null>(null);
  const [stale, setStale] = React.useState(false);
  const dirty = !same(values, baseline);
  const busy = create.isPending || save.isPending;
  const input = { name: values.name, texts: { en: values.en, es: values.es || null } };
  const problem = templateProblem(input, fields, { creating: !template });
  // A field written wrong is said at once; a missing name or text only once Save was pressed.
  const shown = problem && (tried || (problem.place !== "name" && values[problem.place].trim() !== "")) ? problem : null;
  const live = template ? liveUses(template) : [];

  // A tab closed or reloaded with changes not kept asks first.
  React.useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = (patch: Partial<Values>) => {
    setServerProblem(null);
    setValues((v) => ({ ...v, ...patch }));
  };

  const onSave = async () => {
    setTried(true);
    if (problem || busy) return;
    const payload = templatePayload(input);
    try {
      if (!template) {
        const made = await create.mutateAsync({ ...payload, slug: slugFrom(payload.name) });
        toast.success("Template saved", made.name);
        navigate(`/text-templates/${encodeURIComponent(made.slug)}${tenantId ? `?tenant=${tenantId}` : ""}`, { replace: true });
        return;
      }
      const kept = await save.mutateAsync({ ...payload, slug: template.slug, updated_at: readAt });
      const next = valuesOf(kept);
      setBaseline(next);
      setValues(next);
      setReadAt(kept.updated_at);
      setTried(false);
      toast.success(
        "Template saved",
        liveUses(kept).length > 0 ? "Workflows that send it use the new words from now on." : kept.name,
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.message.startsWith(STALE)) {
        setStale(true);
        return;
      }
      setServerProblem(e instanceof ApiError ? e.message : "Could not save the template. Please try again.");
    }
  };

  const loadTheirs = async () => {
    const fresh = await reload.refetch();
    // A refetch that failed still hands back the copy cached before: that is not their version.
    if (!fresh.isSuccess) {
      toast.error("Could not load their version", "Please try again.");
      return;
    }
    const theirs = valuesOf(fresh.data);
    setBaseline(theirs);
    setValues(theirs);
    setReadAt(fresh.data.updated_at);
    setStale(false);
    setServerProblem(null);
  };

  const toggle = async (on: boolean) => {
    if (!template) return;
    try {
      const done = await active.mutateAsync({ slug: template.slug, active: on });
      setReadAt(done.updated_at);
      toast.success(on ? "Template turned on" : "Template turned off", done.name);
    } catch (e) {
      toast.error("Could not change the template", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  return (
    <Page>
      <Link to="/text-templates" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" aria-hidden />
        Text templates
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-4 pb-4">
        <div className="min-w-0 space-y-1">
          <h1 className="text-xl font-semibold tracking-tight text-text">{template ? template.name : "New text template"}</h1>
          <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
            {template ? (
              <>
                <Badge tone={template.active ? "success" : "neutral"} size="sm">
                  {template.active ? "On" : "Off"}
                </Badge>
                <span title={formatDateTime(template.updated_at)}>
                  Saved {formatDateTime(template.updated_at)}
                  {template.updated_by_name && ` by ${template.updated_by_name}`}
                </span>
              </>
            ) : (
              <span>Written once, sent by any workflow that picks it.</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {template && (
            <Tooltip
              content={
                live.length > 0
                  ? `Sent by ${live.map((u) => u.name).join(", ")}. Unpublish it first to turn this off.`
                  : template.active
                    ? "Turn off: no workflow can pick it."
                    : "Turn on: workflows can pick it again."
              }
            >
              <span className="inline-flex items-center gap-2 text-xs text-text-muted">
                <Switch
                  checked={template.active}
                  disabled={active.isPending || (template.active && live.length > 0)}
                  aria-label="Template on"
                  onCheckedChange={(on) => void toggle(on)}
                />
                {template.active ? "On" : "Off"}
              </span>
            </Tooltip>
          )}
          <Button variant="default" disabled={(!dirty && Boolean(template)) || busy} loading={busy} onClick={() => void onSave()}>
            Save
          </Button>
        </div>
      </header>

      <div className="space-y-3 pb-4 empty:hidden">
        {stale && (
          <Callout tone="warning">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              Somebody else saved this template after you opened it, so yours was not saved.
              <Button variant="outline" size="sm" loading={reload.isFetching} onClick={() => void loadTheirs()}>
                Load their version
              </Button>
              <span className="text-text-muted">Your unsaved changes here will be replaced.</span>
            </span>
          </Callout>
        )}
        {serverProblem && <Callout tone="danger">{serverProblem}</Callout>}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-5">
          <Card className="space-y-5 p-5">
            <Field
              label="Name"
              htmlFor="template-name"
              hint={template ? `Workflows pick it as "${template.name}". Its id stays ${template.slug}.` : "What admins see when they pick it in a workflow."}
              error={shown?.place === "name" ? shown.message : undefined}
            >
              <Input
                id="template-name"
                value={values.name}
                maxLength={MAX_NAME_CHARS}
                placeholder="Follow-up after no answer"
                invalid={shown?.place === "name"}
                onChange={(e) => set({ name: e.target.value })}
              />
            </Field>
            <TextBox
              language="en"
              value={values.en}
              fields={fields}
              required
              error={shown?.place === "en" ? shown.message : undefined}
              onChange={(en) => set({ en })}
            />
            <TextBox
              language="es"
              value={values.es}
              fields={fields}
              error={shown?.place === "es" ? shown.message : undefined}
              onChange={(es) => set({ es })}
            />
          </Card>

          {template && (
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-text">Used by</h2>
              {template.used_by.length === 0 ? (
                <p className="mt-1 text-xs text-text-muted">No workflow sends it yet. Pick it in a workflow's Send SMS step.</p>
              ) : (
                <ul className="mt-2 divide-y divide-border">
                  {template.used_by.map((u) => (
                    <li key={u.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                      <Link to={`/workflows/${u.id}`} className="font-medium text-text underline-offset-4 hover:underline">
                        {u.name}
                      </Link>
                      <Badge tone={u.status === "published" ? "success" : "neutral"} size="sm">
                        {u.status === "published" ? "Published" : u.status === "draft" ? "Draft" : "Archived"}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>

        <Preview values={values} fields={fields} />
      </div>
    </Page>
  );
}

/** One language's text, with buttons that put a lead's detail in where the cursor is. */
function TextBox({
  language, value, fields, required = false, error, onChange,
}: {
  language: TemplateLanguage;
  value: string;
  fields: readonly MergeField[];
  required?: boolean;
  error?: string;
  onChange: (text: string) => void;
}) {
  const id = `template-${language}`;
  // Where the cursor was last in the text, so a detail goes in there and not at the end.
  const caret = React.useRef<[number, number] | null>(null);
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const [start, end] = caret.current ?? [value.length, value.length];
    onChange(value.slice(0, start) + token + value.slice(end));
    const at = start + token.length;
    caret.current = [at, at];
    requestAnimationFrame(() => {
      const el = document.getElementById(id) as HTMLTextAreaElement | null;
      el?.focus();
      el?.setSelectionRange(at, at);
    });
  };
  return (
    <Field
      label={`${LANGUAGE_NAME[language]} text${required ? "" : " (optional)"}`}
      htmlFor={id}
      hint={
        <>
          {language === "en"
            ? "Every lead without a Spanish text gets this one."
            : "Spanish-speaking leads get this one. Left empty, they get the English text."}{" "}
          A word after a bar is used when the lead has no value: {"{{first_name|there}}"} says "there".
        </>
      }
      error={error}
      action={
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={`Insert a lead's detail into the ${LANGUAGE_NAME[language]} text`}>
          <Braces className="size-3.5 text-text-subtle" aria-hidden />
          {fields.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => insert(f.key)}
              className="rounded-full border border-border bg-surface px-2 py-0.5 text-[11px] font-medium text-text-muted transition-colors hover:border-accent-8 hover:text-accent-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40"
            >
              + {f.label}
            </button>
          ))}
        </div>
      }
    >
      <Textarea
        rows={4}
        value={value}
        invalid={Boolean(error)}
        className="font-sans text-sm leading-6"
        placeholder={language === "en" ? "Hi {{first_name|there}}, we got your request. Is now a good time for a quick call?" : "Hola {{first_name|}}, recibimos su solicitud. ¿Es buen momento para una llamada?"}
        onChange={(e) => onChange(e.target.value)}
        onSelect={(e) => {
          caret.current = [e.currentTarget.selectionStart, e.currentTarget.selectionEnd];
        }}
      />
    </Field>
  );
}

/** The text as a sample lead gets it: their language's text filled, the opt-out line under a first
 *  text, and how long it is and in how many SMS parts it goes. First or later is how the preview
 *  shows it, never kept with the template (ACL-589): whether a text is a number's first is the
 *  number's, and core-api adds the line then, whichever template it is (ADR-098 in platform). */
function Preview({ values, fields }: { values: Values; fields: readonly MergeField[] }) {
  const [language, setLanguage] = React.useState<TemplateLanguage>("en");
  const [first, setFirst] = React.useState(true);
  const hasSpanish = values.es.trim() !== "";
  const shownLanguage: TemplateLanguage = language === "es" && hasSpanish ? "es" : "en";
  const source = shownLanguage === "es" ? values.es : values.en;
  const text = source.trim() ? asSent(fillText(source, sampleValues(fields)), shownLanguage, first) : "";
  const count = smsParts(text);
  return (
    <div className="lg:sticky lg:top-6 lg:self-start">
      <Card className="space-y-4 p-5">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-text">Preview</h2>
          <Choice
            label="Preview language"
            value={shownLanguage}
            options={[
              { value: "en", label: LANGUAGE_NAME.en },
              { value: "es", label: LANGUAGE_NAME.es, disabled: !hasSpanish },
            ]}
            onChange={setLanguage}
          />
        </div>

        <div className="rounded-2xl bg-surface-sunken p-4">
          {text ? (
            <p className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-accent-9 px-3 py-2 text-sm leading-6 text-white dark:text-gray-1">
              {text}
            </p>
          ) : (
            <p className="flex items-center gap-2 text-xs text-text-muted">
              <MessageSquareText className="size-4" aria-hidden />
              The text shows here as you write it.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-text">Show as</span>
            <Choice
              label="Show the preview as"
              value={first ? "first" : "later"}
              options={[
                { value: "first", label: "First text" },
                { value: "later", label: "Later text" },
              ]}
              onChange={(v) => setFirst(v === "first")}
            />
          </div>
          <p className="text-[11px] text-text-muted">{optOutNote(source, first)}</p>
        </div>

        <dl className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-md border border-border px-3 py-2">
            <dt className="text-text-muted">Characters</dt>
            <dd className="text-sm font-semibold text-text">{count.characters.toLocaleString("en-US")}</dd>
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <dt className="text-text-muted">SMS parts</dt>
            <dd className="text-sm font-semibold text-text">
              {count.parts}
              {count.unicode && <span className="ml-1 text-[11px] font-normal text-text-muted">(Unicode)</span>}
            </dd>
          </div>
        </dl>
        {count.unicode && (
          <p className="text-[11px] text-text-muted">
            A character outside plain SMS letters (an emoji, a curly quote) sends the text at 70 characters a part.
          </p>
        )}

        <div className="border-t border-border pt-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-subtle">Sample lead</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            {fields.map((f) => (
              <React.Fragment key={f.key}>
                <dt className="text-text-muted">{f.label}</dt>
                <dd className="text-text">{f.sample}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      </Card>
    </div>
  );
}

/** The preview's choices, one picked: a row of buttons in a frame. */
function Choice<T extends string>({
  label, value, options, onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border p-0.5" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
            value === o.value ? "bg-accent-2 text-accent-11" : "text-text-muted hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
